import { useMemo } from "react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Upgrade as UpgradeIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { ROW_TYPE } from "../models/RecipeTreeRow";
import type { RecipeTreeRow } from "../models/RecipeTreeRow";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface Props {
  onUpgrade: (row: RecipeTreeRow) => void;
  onClone?: (row: RecipeTreeRow) => void;
  onSetAsCurrent: (row: RecipeTreeRow) => void;
  onDelete: (row: RecipeTreeRow) => void;
  onDeleteFamily: (row: RecipeTreeRow) => void;
  onToggleFamilyEnable: (row: RecipeTreeRow) => void;
}

export const useRecipeTreeTableData = ({
  onUpgrade,
  onClone,
  onSetAsCurrent,
  onDelete,
  onDeleteFamily,
  onToggleFamilyEnable,
}: Props) => {
  const theme = useTheme();

  const columnDefs: ColDef<RecipeTreeRow>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "RecipeName",
        minWidth: 140,
        flex: 1,
        cellStyle: {
          ...cellStyle,
          justifyContent: "flex-start",
          paddingLeft: theme.spacing(1),
        },
        cellRenderer: (params: ICellRendererParams<RecipeTreeRow>) => {
          if (!params.data) return null;

          const { rowType, IsCurrent, IsEnable } = params.data;
          const node = params.node;
          const hasChildren =
            node.allChildrenCount && node.allChildrenCount > 0;
          const isExpanded = node.expanded;

          const level = node.level || 0;
          const indentPx = level * 20;

          const displayName = params.data.displayName;
          const isParent = rowType === ROW_TYPE.PARENT;

          // Show check icon for current version (parent)
          // Green if enabled, gray if disabled
          const icon = IsCurrent ? (
            <CheckCircleIcon
              fontSize="small"
              sx={{ color: IsEnable ? "success.main" : "text.disabled" }}
            />
          ) : null;

          const handleExpandClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            node.setExpanded(!isExpanded);
            params.api.refreshCells({
              rowNodes: [node],
              columns: [params.column!],
              force: true,
            });
          };

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                width: "100%",
                height: "100%",
                pl: `${indentPx}px`,
              }}
            >
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={handleExpandClick}
                  sx={{ p: 0, color: "text.secondary" }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 20 }} />
              )}
              {icon}
              <span
                style={{
                  fontWeight: isParent ? 600 : 400,
                }}
              >
                {displayName}
              </span>
            </Box>
          );
        },
      },
      {
        field: "Author",
        headerName: "Author",
        minWidth: 100,
        cellStyle: cellStyle,
        valueFormatter: (params) => params.value ?? "",
      },
      {
        field: "CreationTimestamp",
        headerName: "Created",
        minWidth: 120,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm") ?? "";
        },
      },
      {
        headerName: "",
        minWidth: 160,
        cellStyle: cellStyle,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<RecipeTreeRow>) => {
          const row = params.data;
          if (!row) return null;

          const isParent = row.rowType === ROW_TYPE.PARENT;

          // Parent row (IsCurrent): toggle enable, delete family, new version, clone
          if (isParent) {
            return (
              <Box
                className="ag-grid-buttons"
                sx={{ display: "flex", gap: 0.5 }}
              >
                {/* Toggle Enable */}
                <Tooltip
                  title={row.IsEnable ? "Disable Recipe" : "Enable Recipe"}
                >
                  <IconButton
                    size="medium"
                    sx={{
                      color: row.IsEnable ? "success.main" : "text.disabled",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFamilyEnable(row);
                    }}
                  >
                    {row.IsEnable ? (
                      <ToggleOnIcon fontSize="small" />
                    ) : (
                      <ToggleOffIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                {/* New Version */}
                <Tooltip title="New Version">
                  <IconButton
                    size="medium"
                    sx={{ color: "primary.main" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpgrade(row);
                    }}
                  >
                    <UpgradeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Clone - only if enabled */}
                {onClone ? (
                  <Tooltip title="Clone">
                    <IconButton
                      size="medium"
                      sx={{ color: "primary.main" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClone(row);
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}

                {/* Delete Family */}
                <Tooltip title="Delete Family">
                  <IconButton
                    size="medium"
                    sx={{ color: "error.main" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFamily(row);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }

          // Child row: set as current, new version, clone, delete
          return (
            <Box className="ag-grid-buttons" sx={{ display: "flex", gap: 0.5 }}>
              {/* Set as Current */}
              <Tooltip title="Set as Current">
                <IconButton
                  size="medium"
                  sx={{ color: "success.main" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetAsCurrent(row);
                  }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* New Version */}
              <Tooltip title="New Version">
                <IconButton
                  size="medium"
                  sx={{ color: "primary.main" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpgrade(row);
                  }}
                >
                  <UpgradeIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Clone - only if enabled */}
              {onClone ? (
                <Tooltip title="Clone">
                  <IconButton
                    size="medium"
                    sx={{ color: "primary.main" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClone(row);
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}

              {/* Delete */}
              <Tooltip title="Delete">
                <IconButton
                  size="medium"
                  sx={{ color: "error.main" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      onUpgrade,
      onClone,
      onSetAsCurrent,
      onDelete,
      onDeleteFamily,
      onToggleFamilyEnable,
      theme,
    ],
  );

  const autoGroupColumnDef: ColDef<RecipeTreeRow> = useMemo(
    () => ({
      headerName: "",
      width: 0,
      maxWidth: 0,
      minWidth: 0,
      resizable: false,
      sortable: false,
      suppressSizeToFit: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: () => null,
      },
    }),
    [],
  );

  return { columnDefs, autoGroupColumnDef };
};
